/**
 * Consolidated configuration for the round suitability scoring system.
 *
 * Every magic number that influences the composite score, weather score,
 * ground-quality score, or daylight score lives here. Consumers
 * (weatherService.js, roundService.js, sunService.js) import these
 * objects rather than duplicating constants inline.
 *
 * This file deliberately mirrors the behaviour of the previous inline
 * constants byte-for-byte. Tuning lives in follow-up commits.
 */

// === COMPOSITE SCORE ===

/**
 * Weights applied in calculateCompositeScore. Sum to 1.0 in each branch.
 *  full           - weather + daylight both available
 *  noWeather      - daylight available, no weather
 *  noDaylight     - weather available, no daylight (rare)
 *  memberOnly     - implicit (memberScore × 1.0) when both are missing
 */
export const COMPOSITE_WEIGHTS = {
  full:       { member: 0.4, weather: 0.4, daylight: 0.2 },
  noWeather:  { member: 0.8, daylight: 0.2 },
  noDaylight: { member: 0.5, weather: 0.5 }
};

/**
 * Member availability uses a non-linear power curve to reward partial
 * attendance: score = (available/total)^exponent × 10.
 * exponent < 1 lifts the middle (50% attendance ≈ 6.6 instead of 5.0).
 */
export const MEMBER_CURVE = {
  exponent: 0.6
};

/**
 * Hard cap on the final composite score ("no perfect 10").
 */
export const COMPOSITE_SCORE_CAP = 9.9;

// === WEATHER SUB-CURVES ===

/**
 * Smooth-curve parameters for each weather sub-score in
 * calculateSimpleGolfScore. All sub-scores produce values in [0, 1].
 *
 * temp:     Gaussian        exp(-((t-centre)/sigma)^2)
 *           sigma=9 keeps a UK winter round (8 °C) playable (~0.34) instead
 *           of being scored as harshly as the old sigma=7 (~0.14).
 *
 * wind:     Logistic decay  1 / (1 + exp((w-midpoint)/steepness))
 *           midpoint=20 mph matches the golf-playability literature; 15 mph
 *           is "breezy but fine" and now scores ~0.73 instead of 0.50.
 *
 * precip:   Reciprocal      1 / (1 + (p/scale)^exponent)
 *
 * humidity: One-sided sigmoid  1 / (1 + exp((h-midpoint)/steepness))
 *           Replaces the symmetric parabola which wrongly penalised low RH.
 *           At 30 % ≈ 1.0, at 60 % ≈ 0.87, at 75 % = 0.50, at 85 % ≈ 0.22.
 */
export const WEATHER_CURVES = {
  temp:     { centre: 21, sigma: 9 },
  wind:     { midpoint: 20, steepness: 5 },
  precip:   { scale: 1.5, exponent: 1.5 },
  humidity: { midpoint: 75, steepness: 8 }
};

/**
 * Source-aware sub-score weights. When the precipitation value is for
 * "rain during round" (hourly data) we trust the rain timing and lean
 * harder on it. For daily totals we balance back into the other factors.
 */
export const WEATHER_WEIGHTS = {
  hourly: { temp: 0.25, wind: 0.20, precip: 0.50, humidity: 0.05 },
  daily:  { temp: 0.35, wind: 0.30, precip: 0.30, humidity: 0.05 }
};

/**
 * 24h daily rain total ÷ 4h round window = 6 slot-equivalents per day.
 * Used to normalise daily-source precip into the same scale as
 * round-window precip before applying the precip curve.
 */
export const DAILY_TO_ROUND_RATIO = 6;

// === SIX-TIER CATEGORY SYSTEM (shared across weather / daylight / composite) ===

/**
 * Convert a 0-10 score into the shared 6-tier category bucket.
 * Used for badge colour-coding and human-readable status strings.
 */
export function categoriseScore(score) {
  if (score >= 9.0) return 'excellent';
  if (score >= 8.0) return 'very-good';
  if (score >= 7.0) return 'good';
  if (score >= 5.0) return 'fair';
  if (score >= 3.0) return 'poor';
  return 'severe';
}

// === GROUND QUALITY ===

/**
 * Three-phase ground-quality model:
 *   1. R_eff      = Σ rain[i] × decayAlpha^i  (i = 0..lookbackDays-1)
 *   2. S_soft     = 1 - exp(-R_eff / saturationThreshold)   (clamped at saturationCap×)
 *   3. G          = 1.0 if S_soft ≤ firmThreshold else 1 / (1 + (x/penaltyShape)^2)
 *                  where x = (S_soft - firmThreshold) / (1 - firmThreshold)
 *
 * Constants tuned for UK parkland courses. See plans/251211_ground-softness-v2.md.
 */
export const GROUND_CONFIG = {
  lookbackDays: 7,
  // α bumped from 0.7 → 0.85 because ET0 is now subtracted from each
  // day's rain directly (no need to also discount in the decay). 0.85
  // gives drainage half-life of ~4 days, consistent with typical UK
  // parkland field-capacity recovery times.
  decayAlpha: 0.85,
  saturationThreshold: 20,
  saturationCap: 1.5,
  firmThreshold: 0.25,
  penaltyShape: 0.5,
  categories: {
    firm: 9.0,
    soft: 6.0,
    wet: 3.0,
    waterlogged: 0
  },
  frozenTemp: 0
};

/**
 * Intensity multiplier for heavy single-day events. A 30 mm deluge in
 * one day saturates the soil and runs off more than 30 mm spread over a
 * week - the formula needs to see "this was a flood" vs "this was drizzle".
 *
 *   intensity = 1 + max(0, (rain_day - threshold) / divisor)
 *   capped at max
 *
 * Defaults:
 *   - rain_day ≤ 15 mm:   intensity = 1.0 (no penalty)
 *   - rain_day = 25 mm:   intensity = 1.33
 *   - rain_day = 45 mm:   intensity = 2.0 (cap)
 *   - rain_day = 100 mm:  intensity = 2.0 (cap)
 *
 * Applied to net_rain (after ET subtraction) since intensity is about
 * how the rain *fell*, not how much remained.
 */
export const INTENSITY_CONFIG = {
  threshold: 15,
  divisor: 30,
  max: 2.0
};

/**
 * Round-day ET0 estimator (used when the round day isn't yet in the
 * Open-Meteo cache). Simplified FAO-style heuristic combining temperature,
 * humidity, and wind. Produces mm/day matching typical Open-Meteo ET0
 * values within ±1 mm across the UK seasonal range.
 *
 *   ET0_est ≈ base + tempCoef × max(0, T - tempZero)
 *                  + windCoef × W
 *                  - humCoef  × max(0, RH - humThresh)
 *   clamped to [min, max]
 *
 * For Leyland this week: predicted ET0 vs Open-Meteo actual differs by
 * <1 mm/day. Cheap fallback - the real ET0 from Open-Meteo is used
 * whenever available.
 */
export const ET0_ESTIMATE = {
  base: 1.0,
  tempCoef: 0.18,
  tempZero: 5,
  windCoef: 0.08,
  humCoef: 0.04,
  humThresh: 60,
  min: 0.3,
  max: 8.0
};

/**
 * Asymmetric ground integration: ground quality can DRAG the weather score
 * down (when the course is genuinely wet) but cannot LIFT it (firm ground
 * does not rescue a 33 °C heatwave or a 28 mph gale).
 *
 *   blended  = weather × (1 - GROUND_DRAG_WEIGHT) + ground × GROUND_DRAG_WEIGHT
 *   adjusted = min(weather, blended)
 *
 * Higher drag weight = wet ground penalises the composite harder. Since
 * the asymmetric blend removes the "firm ground rescues bad weather"
 * concern, 0.30 captures meaningful wet-day penalties (mild day after
 * monsoon: 9.3 → 7.5) without the false-positive risk of the prior
 * symmetric model.
 */
export const GROUND_DRAG_WEIGHT = 0.30;

/**
 * Frozen ground (T ≤ frozenTemp) short-circuits to this score. Capped
 * below 10 because frozen ground can be icy and hazardous.
 */
export const FROZEN_GROUND_SAFETY_SCORE = 8.0;

// === DAYLIGHT ===

/**
 * Daylight scoring in calculateDaylightScore:
 *   overlap subscore (overlapWeight):    fraction of round inside safe window
 *   comfort subscore (comfortWeight):    symmetric logistic on how far the round
 *                                        midpoint sits from BOTH daylight edges
 *                                        (min of the sunrise-side and sunset-side
 *                                        logistics) - collapses near either edge
 *   safe window:                         [sunrise + safeStartOffsetMin, sunset + safeEndOffsetMin]
 *   logistic:                            1 / (1 + exp(-steepness × (hours - midpointHours)))
 *   neutralScore:                        returned when sun data is unavailable
 *
 * Since the full 24h day is now searched (see generateTimeWindows), the comfort
 * subscore is SYMMETRIC: the old model only penalised the sunset side, so a
 * pre-dawn round (overlap ≈ 0) still scored ≈ full marks on proximity and could
 * surface as "good". The symmetric term drives genuinely dark rounds toward 0.
 */
export const DAYLIGHT_CONFIG = {
  safeStartOffsetMin: 30,
  safeEndOffsetMin: -15,
  overlapWeight: 0.7,
  comfortWeight: 0.3,
  proximityLogistic: { steepness: 1.5, midpointHours: 1.5 },
  neutralScore: 7,
  // Hard cap when the round starts before sunrise OR finishes after sunset.
  // The smooth-curve subscores under-penalise these cases; the cap makes
  // sure such a round can never present as "good" or better.
  hardCapScore: 5.0,
  // Smart-search safety net: a round whose daylight overlap fraction is at or
  // below this is treated as effectively un-daylit and filtered out of results
  // entirely (see roundService). Catches the dead of night even when daytime
  // alternatives are scarce; scoring still ranks everything above it.
  minPlayableOverlapFraction: 0.25,
  // Tee-off gate: overlap alone lets a dead-of-night tee-off survive at high
  // summer latitudes (a 03:00 start at a UK links still overlaps ~44% of the
  // safe window), so additionally drop any slot whose TEE-OFF is more than
  // this many minutes before sunrise. 30 keeps genuine first-light rounds.
  maxTeeOffBeforeSunriseMin: 30
};

// === UNPLAYABLE THRESHOLDS ===

/**
 * Hard thresholds above which conditions are filtered out of smart-search
 * results entirely. Applied in scoreTimeSlots; see weatherData.isUnplayable.
 */
export const UNPLAYABLE_THRESHOLDS = {
  precipitationDuringRound: 10,
  precipitationOnDay: 40,
  windSpeed: 35,
  temperature: { min: 0, max: 40 },
  groundQuality: 2.5
};
